import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { StepChain } from '@/components/admin/sessions/walk/step-chain';
import { pageWindowLabel, plural } from '@/components/admin/sessions/walk/walk-labels';
import { protocolClosingSentence, seatModels } from '@/lib/conversation-model';
import { costLabel, tokensLabel } from '@/lib/session-labels';
import { pageAnchor } from '@/lib/session-toc';
import { cn } from '@/lib/utils';
import {
  type SessionDetail,
  type SessionWalkPage,
  sessionFigures,
  stepRuns,
} from '@matter_extractor/shared';
import { Icon, buttonVariants } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';

/**
 * The protocol page of the walk (§12.2) - the one block that carries the bleu
 * nuit border, and the tallest card of the screen.
 *
 * The hierarchy is the grill's, in one sentence: « le protocole est la partie la
 * plus importante de l'expérience ». Everything else on this screen is a grey
 * card of two lines, so the border is not decoration - it is the only thing that
 * says which of six cards is the one somebody came for. It is also why no other
 * component of this folder may take a `border-bleu-nuit`: a second one would
 * spend the distinction.
 *
 * ## It says so when it is empty, and when it was never reached
 *
 * « Une page protocole vide, ou jamais atteinte, le DIT plutôt que de
 * disparaître » (§12.2). The two are not the same sentence and the screen can
 * tell them apart by one fact: a cascade that froze NO protocol is an experience
 * whose protocol page was left empty (§5.2), while a frozen protocol with no
 * turn is a conversation that never started - on a page the session did not
 * reach, or reached and left without a word. A session with no turn at all has
 * to give a readable screen, and this block is most of that screen.
 *
 * ## What is NOT here
 *
 * The step budget of §11.2 that the maquette prints beside the cost. It is a
 * setting of Réglages, not something a session records, so it would take a
 * second request - and §12.3 already puts « le cumul de l'étape contre le
 * budget » in the Appels layer of a turn, which is where the question is asked.
 */

export interface WalkProtocolBlockProps {
  readonly detail: SessionDetail;
  /** The visit the turns were played in; null when no visit holds any. */
  readonly page: SessionWalkPage | null;
  /** 1-based rank in the walk, null on a block that stands outside it. */
  readonly rank: number | null;
  /** Ringed a moment: the card a line of the Variables tab led back to (#172). */
  readonly flash?: boolean;
}

export function WalkProtocolBlock({ detail, page, rank, flash = false }: WalkProtocolBlockProps) {
  const { cascade, session } = detail;
  // The block prints the PROTOCOL's cost and tokens, its turns' - what its
  // chain of steps sums to. The analyst of the noyau (#149) is the session's,
  // counted in the tiles of the head and never here: it plays no turn.
  const figures = sessionFigures(detail);
  const runs = stepRuns(detail);
  const seats = seatModels(detail.turns);
  const closing = protocolClosingSentence(detail);
  const played = detail.turns.length > 0;
  /*
    Only the half that is not zero. « 0 incident · 1 tour malformé » spends a
    line saying nothing happened beside a line saying something did, and the
    yellow is supposed to point at exactly one thing.
  */
  const defects = [
    figures.incidents > 0 ? plural(figures.incidents, 'incident') : null,
    figures.malformed > 0 ? plural(figures.malformed, 'tour malformé', 'tours malformés') : null,
  ].filter((part): part is string => part !== null);

  return (
    <article
      // The anchor of the sommaire (#221): the card of its visit, or its own when no visit holds it.
      id={pageAnchor(rank)}
      data-testid={SESSIONS_TEST_IDS.walkProtocol}
      data-page-id={page?.pageId}
      // The index of the visit, which the Variables tab leads back to (#172).
      data-visit={rank === null ? undefined : rank - 1}
      data-flash={flash ? 'true' : undefined}
      className={cn(
        'overflow-hidden rounded-md border-2 border-bleu-nuit bg-white',
        flash ? 'pms-flash' : null,
      )}
    >
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 bg-bleu-nuit-tint px-4 py-2.5">
        <span className="font-bold text-bleu-nuit text-micro uppercase tracking-wide">
          {rank === null ? 'Page protocole' : `Page ${rank}`}
        </span>
        <h3 className="font-bold text-bleu-nuit text-small">{page?.name ?? 'Protocole'}</h3>
        {page === null ? null : (
          <span className="ml-auto whitespace-nowrap text-bleu-nuit-deep text-caption tabular-nums">
            {pageWindowLabel(page)}
          </span>
        )}
      </header>

      <div className="flex flex-col gap-3.5 px-4 py-3">
        <p className="flex flex-wrap gap-x-5 gap-y-2 text-caption text-gray-600">
          <span>
            Protocole{' '}
            {cascade.protocol === null ? (
              <b className="font-semibold text-gray-900">aucun</b>
            ) : (
              <>
                <b className="font-semibold text-gray-900">{cascade.protocol.name}</b> v
                {cascade.protocol.versionNumber}
              </>
            )}
          </span>
          <span>
            Persona{' '}
            {cascade.persona === null ? (
              <b className="font-semibold text-gray-900">aucun</b>
            ) : (
              <>
                <b className="font-semibold text-gray-900">{cascade.persona.name}</b> v
                {cascade.persona.versionNumber}
              </>
            )}
          </span>
          {seats.interviewer === null ? null : (
            <span>
              Interviewer <b className="font-semibold text-gray-900">{seats.interviewer}</b>
            </span>
          )}
          {seats.scribe === null ? null : (
            <span>
              Scribe <b className="font-semibold text-gray-900">{seats.scribe}</b>
            </span>
          )}
        </p>

        {runs.length === 0 ? null : <StepChain runs={runs} />}

        {played ? (
          <p className="flex flex-wrap items-center gap-x-6 gap-y-2 text-caption text-gray-700">
            <span>
              <b className="font-bold text-gray-900 tabular-nums">{figures.turnCount}</b>{' '}
              {figures.turnCount >= 2 ? 'tours' : 'tour'}
            </span>
            <span>
              <b className="font-bold text-gray-900 tabular-nums">
                {costLabel(figures.protocolCostEur)}
              </b>
            </span>
            <span>
              <b className="font-bold text-gray-900 tabular-nums">
                {tokensLabel(figures.protocolTokens)}
              </b>{' '}
              jetons
            </span>
            <span>
              <b className="font-bold text-gray-900 tabular-nums">{figures.objectivesSucceeded}</b>{' '}
              {figures.objectivesSucceeded >= 2 ? 'objectifs réussis' : 'objectif réussi'} sur{' '}
              <b className="font-bold text-gray-900 tabular-nums">{figures.objectivesTotal}</b>
            </span>
            {defects.length === 0 ? null : (
              <span className="inline-flex items-center gap-1.5 font-semibold text-jaune-deep">
                <Icon name="warning" size={14} />
                {defects.join(' · ')}
              </span>
            )}
          </p>
        ) : null}

        {closing === null ? null : (
          <p className="text-caption text-gray-600">
            {closing.lead}
            <b className="font-semibold text-gray-900">{closing.emphasis}</b>
            {closing.tail}
          </p>
        )}

        {played ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Link
              to="/admin/sessions/$sessionId/protocole"
              params={{ sessionId: session.id }}
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
            >
              Ouvrir la conversation
              <Icon name="chevron_right" size={16} />
            </Link>
            <span className="text-caption text-gray-600">
              Les {plural(figures.turnCount, 'tour')}, le raisonnement du scribe et du moteur, les
              appels et l’état du protocole à chaque tour.
            </span>
          </div>
        ) : (
          /*
            No link, because there is nothing behind it: a button that opens an
            empty transcript is a promise the next screen cannot keep. The
            sentence names which of the two silences this is instead.
          */
          <p className="text-caption text-gray-600">
            {cascade.protocol === null
              ? 'Cette expérience n’a figé aucun protocole : la page Protocole était vide, et aucune conversation n’a eu lieu.'
              : 'Aucun tour n’a été joué : la page Protocole n’a pas été atteinte, ou la session l’a quittée sans qu’un mot soit échangé.'}
          </p>
        )}
      </div>
    </article>
  );
}
